<!-- api-response-dialog.component.html -->
<div class="dialog-container">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <div class="header-info">
      <h2 mat-dialog-title class="dialog-title">API Response</h2>
      <div class="response-summary">
        <!-- badges -->
        <span class="status-badge" [class]="getStatusClass()">
          {{ getStatusCode() }} {{ getStatusText() }}
        </span>
        <span class="time-badge">{{ data.time }}</span>
        <span class="size-badge">{{ formatSize(data.size) }}</span>
      </div>
    </div>
  </div>
  

  <!-- Dialog Content -->
  <div mat-dialog-content class="dialog-content">
    <!-- Main tabs -->
    <div class="main-tabs">
      <button
        mat-button
        class="main-tab"
        [class.active]="activeTab === 'body'"
        (click)="setActiveTab('body')"
      >
        <mat-icon>description</mat-icon>
        <span>Body</span>
      </button>

      <button
        mat-button
        class="main-tab"
        [class.active]="activeTab === 'headers'"
        (click)="setActiveTab('headers')"
      >
        <mat-icon>list</mat-icon>
        <span>Headers {{ getTabCountText("headers") }}</span>
      </button>

      <button
        mat-button
        class="main-tab"
        [class.active]="activeTab === 'cookies'"
        (click)="setActiveTab('cookies')"
      >
        <mat-icon>cookie</mat-icon>
        <span>Cookies {{ getTabCountText("cookies") }}</span>
      </button>

      <button
        mat-button
        class="main-tab"
        [class.active]="activeTab === 'status'"
        (click)="setActiveTab('status')"
      >
        <mat-icon>info</mat-icon>
        <span>Status</span>
      </button>
    </div>

    <!-- Content display area -->
    <div class="tab-content">
      <!-- Body Tab -->
      <div
        *ngIf="activeTab === 'body'"
        class="tab-panel"
        [class.empty-body]="!getBodyString()"
      >
        <!-- Format selector - only show if there's content -->
        <div class="format-selector" *ngIf="getBodyString()">
          <button
            mat-button
            class="format-tab template-generator-action"
            (click)="openTemplateGenerator()"
            *ngIf="canGenerateTemplate()"
            matTooltip="Build a validation template from this response, for the 'validate response template' / 'verify response matches template' commands"
          >
            <mat-icon>rule</mat-icon> <span>Generate Validation Template</span>
          </button>

          <button
            mat-button
            class="format-tab"
            [class.active]="bodyFormat === 'pretty'"
            (click)="setBodyFormat('pretty')"
            *ngIf="isJsonContent(getBodyString())"
          >
            <mat-icon>code</mat-icon> <span>Pretty</span>
          </button>

          <button
            mat-button
            class="format-tab"
            [class.active]="bodyFormat === 'raw'"
            (click)="setBodyFormat('raw')"
          >
            <mat-icon>text_fields</mat-icon> <span>Raw</span>
          </button>

          <button
            mat-button
            class="format-tab"
            [class.active]="bodyFormat === 'xml'"
            (click)="setBodyFormat('xml')"
            *ngIf="isXmlContent(getBodyString())"
          >
            <mat-icon>code</mat-icon> <span>XML</span>
          </button>

          <button
            mat-button
            class="format-tab"
            [class.active]="bodyFormat === 'html'"
            (click)="setBodyFormat('html')"
            *ngIf="isHtmlContent(getBodyString())"
          >
            <mat-icon>language</mat-icon> <span>HTML</span>
          </button>

          <button
            mat-button
            class="format-tab"
            [class.active]="bodyFormat === 'js'"
            (click)="setBodyFormat('js')"
            *ngIf="isJavaScriptContent(getBodyString())"
          >
            <mat-icon>integration_instructions</mat-icon> <span>JS</span>
          </button>

          <button
            mat-button
            class="format-tab"
            [class.active]="bodyFormat === 'base64'"
            (click)="setBodyFormat('base64')"
          >
            <mat-icon>vpn_key</mat-icon> <span>Base64</span>
          </button>

          <button
            mat-button
            class="format-tab"
            [class.active]="bodyFormat === 'hex'"
            (click)="setBodyFormat('hex')"
          >
            <mat-icon>memory</mat-icon> <span>Hex</span>
          </button>
        </div>

        <!-- Body content -->
        <div
          class="content-area"
          [class.has-content]="getBodyString()"
          [class.empty-state]="!getBodyString()"
        >
          <!-- Content sections - only show if there's content -->
          <div *ngIf="getBodyString()" class="format">
            <div *ngIf="bodyFormat === 'pretty'" class="content-section">
              <pre>{{ formatJson(getBodyString()) }}</pre>
            </div>
            <div *ngIf="bodyFormat === 'raw'" class="content-section">
              <textarea
                class="raw-textarea"
                [value]="getBodyString()"
                readonly
              ></textarea>
            </div>
            <div *ngIf="bodyFormat === 'xml'" class="content-section">
              <pre>{{ formatXml(getBodyString()) }}</pre>
            </div>
            <div *ngIf="bodyFormat === 'html'" class="content-section">
              <pre>{{ formatHtml(getBodyString()) }}</pre>
            </div>
            <div *ngIf="bodyFormat === 'js'" class="content-section">
              <pre>{{ formatJs(getBodyString()) }}</pre>
            </div>
            <div *ngIf="bodyFormat === 'base64'" class="content-section">
              <pre>{{ getBase64Content(getBodyString()) }}</pre>
            </div>
            <div *ngIf="bodyFormat === 'hex'" class="content-section">
              <pre>{{ formatHex(getBodyString()) }}</pre>
            </div>
          </div>

          <!-- Empty state -->
          <div *ngIf="!getBodyString()" class="empty-content">
            <mat-icon>inbox</mat-icon>
            <p>No response body</p>
          </div>
        </div>
      </div>

      <!-- Headers Tab -->
      <div *ngIf="activeTab === 'headers'" class="tab-panel">
        <div class="content-area">
          <div *ngIf="hasHeaders()" class="headers-content">
            <div class="header-item" *ngFor="let header of getHeaders()">
              <div class="header-key">{{ header.key }}</div>
              <div class="header-value">{{ header.value }}</div>
            </div>
          </div>
          <div *ngIf="!hasHeaders()" class="empty-content">
            <mat-icon>list</mat-icon>
            <p>No headers</p>
          </div>
        </div>
      </div>

      <!-- Cookies Tab -->
      <div *ngIf="activeTab === 'cookies'" class="tab-panel">
        <div class="content-area">
          <div *ngIf="hasCookies()" class="cookies-content">
            <div class="cookie-item" *ngFor="let cookie of data.cookies">
              <pre>{{ cookie | json }}</pre>
            </div>
          </div>
          <div *ngIf="!hasCookies()" class="empty-content">
            <mat-icon>cookie</mat-icon>
            <p>No cookies</p>
          </div>
        </div>
      </div>

      <!-- Status Tab -->
      <div *ngIf="activeTab === 'status'" class="tab-panel">
        <div class="content-area">
          <div class="status-content">
            <div class="status-item">
              <div class="status-label">
                <mat-icon>info</mat-icon>
                Status Code
              </div>
              <div class="status-value">
                <span class="status-code" [class]="getStatusClass()">
                  {{ getStatusCode() }} {{ getStatusText() }}
                </span>
              </div>
            </div>

            <div class="status-item">
              <div class="status-label">
                <mat-icon>schedule</mat-icon>
                Response Time
              </div>
              <div class="status-value">{{ data.time }}</div>
            </div>

            <div class="status-item">
              <div class="status-label">
                <mat-icon>storage</mat-icon>
                Response Size
              </div>
              <div class="status-value">{{ formatSize(data.size) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Response Validation Template Generator overlay -->
  <div class="template-generator-overlay" *ngIf="showTemplateGenerator">
    <div class="template-generator-panel">
      <div class="template-generator-header">
        <span>Validation Template</span>
        <span class="template-generator-hint">
          Every field starts as NOT_NULL — edit the ones you care about, then
          copy this into the Value field of "validate response template"
          or "verify response matches template".
        </span>
      </div>
      <textarea
        class="raw-textarea template-generator-textarea"
        [(ngModel)]="generatedTemplate"
        spellcheck="false"
      ></textarea>
      <div class="template-generator-actions">
        <span class="template-save-error" *ngIf="templateSaveError">{{ templateSaveError }}</span>
        <button
          mat-button
          *ngIf="canSaveTemplateToApiObject()"
          [disabled]="savingTemplate"
          (click)="saveTemplateToApiObject()"
          matTooltip="Save this template on the '{{ data.apiObjectName }}' API Object, so any step can reuse it"
        >
          <mat-icon>save</mat-icon>
          <span>{{ templateSaved ? 'Saved!' : (savingTemplate ? 'Saving...' : 'Save to API Object') }}</span>
        </button>
        <button mat-button (click)="copyGeneratedTemplate()">
          <mat-icon>content_copy</mat-icon>
          <span>{{ templateCopied ? 'Copied!' : 'Copy' }}</span>
        </button>
        <button mat-button (click)="closeTemplateGenerator()">
          <mat-icon>close</mat-icon>
          <span>Close</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Dialog Actions -->
  <div mat-dialog-actions class="dialog-actions">
    <button
      mat-raised-button
      color="primary"
      (click)="onClose()"
      class="close-action"
    >
      Close
    </button>
  </div>
</div>
